import { transform } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import type OlGeometry from 'ol/geom/Geometry';

/**
 * Calculates the proper extent for a vector layer, handling antimeridian crossings.
 *
 * When features cross the 180th meridian (antimeridian), OpenLayers' default
 * extent calculation fails because it tries to create a bounding box spanning
 * from ~178° to ~-179°, which covers almost the entire world instead of the
 * small area across the date line.
 *
 * This function detects antimeridian crossings and calculates the extent
 * in normalized longitude space to get the correct geographic bounds.
 *
 * @param source - The vector source containing features
 * @param displayProjection - The display projection (e.g., 'EPSG:3857')
 * @param dataProjection - The data projection (e.g., 'EPSG:4326')
 * @returns The calculated extent in map projection coordinates [minX, minY, maxX, maxY]
 */
export function calculateExtentWithAntimeridianSupport(
  source: VectorSource<Feature<OlGeometry>>,
  displayProjection: string,
  dataProjection: string
): number[] | null {
  const features = source.getFeatures();

  if (features.length === 0) {
    return null;
  }

  // Helper function to normalize longitude for antimeridian calculations
  const normalizeLongitude = (lon: number, baseLon: number): number => {
    while (lon - baseLon > 180) lon -= 360;
    while (baseLon - lon > 180) lon += 360;
    return lon;
  };

  // Extract ALL coordinates from all features first
  const allCoordinates: number[][] = [];

  features.forEach((feature) => {
    const geometry = feature.getGeometry();
    if (!geometry) return;

    // Extract coordinates based on geometry type
    let coordinates: number[][] = [];
    const geomType = geometry.getType();

    if (geomType === 'Point') {
      const point = geometry as any;
      coordinates = [point.getCoordinates()];
    } else if (geomType === 'LineString') {
      const lineString = geometry as any;
      coordinates = lineString.getCoordinates();
    } else if (geomType === 'Polygon') {
      const polygon = geometry as any;
      coordinates = polygon.getCoordinates()[0]; // Outer ring
    } else if (geomType === 'MultiPoint') {
      const multiPoint = geometry as any;
      coordinates = multiPoint.getCoordinates();
    } else if (geomType === 'MultiLineString') {
      const multiLineString = geometry as any;
      coordinates = multiLineString.getCoordinates().flat();
    } else if (geomType === 'MultiPolygon') {
      const multiPolygon = geometry as any;
      coordinates = multiPolygon.getCoordinates().flat(2);
    } else if (geomType === 'Circle') {
      // Handle Circle geometry - get center point and use it for extent calculation
      const circle = geometry as any;
      const center = circle.getCenter();
      coordinates = [center];
    }

    // Transform coordinates from map projection back to WGS84 for longitude analysis
    const transformedCoords = coordinates.map((coord) => {
      const [lon, lat] = transform(coord, displayProjection, dataProjection);
      return [lon, lat];
    });

    allCoordinates.push(...transformedCoords);
  });

  if (allCoordinates.length === 0) {
    return null;
  }

  // Check for antimeridian crossing at the COLLECTION level
  // Find the longitude range of all coordinates
  const longitudes = allCoordinates.map((coord) => coord[0]);
  const minLonRaw = Math.min(...longitudes);
  const maxLonRaw = Math.max(...longitudes);

  // If the raw longitude span is > 180°, we likely have antimeridian crossing
  // This happens when we have coordinates on both sides of the 180° meridian
  const hasAntimeridianCrossing = maxLonRaw - minLonRaw > 180;

  if (hasAntimeridianCrossing) {
    // Handle antimeridian crossing: calculate extent in normalized longitude space
    const firstLon = allCoordinates[0][0];
    let minLon = firstLon;
    let maxLon = firstLon;
    let minLat = allCoordinates[0][1];
    let maxLat = allCoordinates[0][1];

    allCoordinates.forEach(([lon, lat]) => {
      const normalizedLon = normalizeLongitude(lon, firstLon);
      minLon = Math.min(minLon, normalizedLon);
      maxLon = Math.max(maxLon, normalizedLon);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });

    // Ensure longitude values are in valid range [-180, 180]
    while (minLon < -180) {
      minLon += 360;
      maxLon += 360;
    }
    while (maxLon > 180) {
      minLon -= 360;
      maxLon -= 360;
    }

    // Transform back to map projection
    const [minX, minY] = transform(
      [minLon, minLat],
      dataProjection,
      displayProjection
    );
    const [maxX, maxY] = transform(
      [maxLon, maxLat],
      dataProjection,
      displayProjection
    );

    return [minX, minY, maxX, maxY];
  } else {
    // Use default OpenLayers extent calculation for normal cases
    return source.getExtent();
  }
}
